import { z } from 'zod'
import { Block, DomeinSleutel, PublicatieId, RondeSleutel } from './id.js'
import { Moment } from './moment.js'

/**
 * Een ronde die het processysteem vastlegt (D-75, D-67, D-41; ontwerp "Ronde uit het processysteem").
 *
 * Het processysteem maakt de ronde en meldt dat aan eFormulieren; vastleggen is niet publiceren. De id's zijn
 * die van het processysteem: een id voor de ronde en een id per plek, dat is per combinatie van bouwblok en
 * uitvraagmoment (besluit 7). Eén uitvraagmoment voor drie bouwblokken zijn dus drie plekken met drie id's.
 * Een eigen bouwblok krijgt zijn contract uit de registry in versiebeheer; het XSD van een bouwblok van buiten
 * en het dossier-contract komen in het bericht (besluit 5).
 */

/**
 * Een id van het processysteem (besluit 7): een GUID past, witruimte en XML-tekens niet. Eén soort tekst
 * voor het id van een ronde, een plek en een zaak; het id is opaak, eFormulieren leest er niets in. In de XML
 * staat het met `datatype="guid"`, zoals `dossierId`.
 */
export const ProcessysteemId = z.string().regex(/^[A-Za-z0-9][A-Za-z0-9._:-]{0,99}$/, 'een id van het processysteem is een GUID of een korte tekst zonder witruimte')
export type ProcessysteemId = z.infer<typeof ProcessysteemId>

/**
 * Het id van een zaak bij het processysteem (besluit 26), voor een herhaalbare plek: elke zaak is een eigen
 * lijn op dat dossier, bouwblok en die uitvraag. Dezelfde vorm als elk ander id van het processysteem.
 */
export const ZaakId = ProcessysteemId
export type ZaakId = z.infer<typeof ZaakId>

/**
 * Het uitvraagmoment van een plek (besluit 6): kleine letters, cijfers, punt en koppelteken, tot 40 tekens.
 * Een aanbod bevriest het en het gaat het podmanifest in (D-67), dus de vorm is smal.
 */
export const UitvraagAanduiding = z.string().regex(/^[a-z0-9][a-z0-9.-]{0,39}$/, 'een uitvraagmoment is kleine letters, cijfers, punt en koppelteken, tot 40 tekens')
export type UitvraagAanduiding = z.infer<typeof UitvraagAanduiding>

/** Het uitvraagmoment van een bouwblok in het bericht: het id van de plek en de aanduiding. */
const Uitvraagmoment = z.strictObject({
  plekId: ProcessysteemId,
  aanduiding: UitvraagAanduiding,
  /** Ontbreekt is `false` (besluit 21): niet herhaalbaar is de dichte kant, één lijn per dossier. */
  herhaalbaar: z.boolean().optional(),
})

/**
 * Het bericht dat een ronde vastlegt (besluit 3 tot en met 7, 21 en 22).
 *
 * De rondesleutel houdt de vorm `jjjj-n`, ook bij een doorlopende ronde (besluit 3). Een bouwblok staat
 * hoogstens één keer in de ronde, in de volgorde van het bericht. Een eigen bouwblok draagt geen `xsd` en
 * minstens één uitvraagmoment; een blok van buiten draagt een `xsd` en geen uitvraagmoment. Die twee regels
 * toetst de opslag, want ze hangen af van de registry. Hier staan de vormregels die zonder opslag te toetsen
 * zijn (besluit 22): het penvoerende domein werkt niet ook mee, een domein, blok, plek-id of aanduiding bij
 * één blok staat er niet twee keer.
 */
export const RondeVastleggenRequest = z
  .strictObject({
    rondeId: ProcessysteemId,
    sleutel: RondeSleutel,
    penvoerendDomein: DomeinSleutel,
    meewerkendeDomeinen: z.array(DomeinSleutel),
    doorlopend: z.boolean(),
    bouwblokken: z
      .array(
        z.strictObject({
          blok: Block,
          xsd: z.string().min(1).optional(),
          uitvraagmomenten: z.array(Uitvraagmoment),
        }),
      )
      .min(1),
    dossierXsd: z.string().min(1).optional(),
  })
  .superRefine((request, ctx) => {
    const domeinen = new Set<string>()
    request.meewerkendeDomeinen.forEach((domein, i) => {
      if (domein === request.penvoerendDomein) {
        ctx.addIssue({ code: 'custom', path: ['meewerkendeDomeinen', i], message: `${domein} is het penvoerende domein en werkt dus niet mee` })
      }
      if (domeinen.has(domein)) ctx.addIssue({ code: 'custom', path: ['meewerkendeDomeinen', i], message: `${domein} staat er twee keer` })
      domeinen.add(domein)
    })
    const blokken = new Set<string>()
    const plekken = new Set<string>()
    request.bouwblokken.forEach((bouwblok, i) => {
      if (blokken.has(bouwblok.blok)) {
        ctx.addIssue({ code: 'custom', path: ['bouwblokken', i, 'blok'], message: `${bouwblok.blok} staat er twee keer` })
      }
      blokken.add(bouwblok.blok)
      const aanduidingen = new Set<string>()
      bouwblok.uitvraagmomenten.forEach((moment, j) => {
        if (plekken.has(moment.plekId)) {
          ctx.addIssue({ code: 'custom', path: ['bouwblokken', i, 'uitvraagmomenten', j, 'plekId'], message: `plek ${moment.plekId} staat er twee keer` })
        }
        plekken.add(moment.plekId)
        if (aanduidingen.has(moment.aanduiding)) {
          ctx.addIssue({ code: 'custom', path: ['bouwblokken', i, 'uitvraagmomenten', j, 'aanduiding'], message: `uitvraagmoment ${moment.aanduiding} staat er twee keer bij ${bouwblok.blok}` })
        }
        aanduidingen.add(moment.aanduiding)
      })
    })
  })
export type RondeVastleggenRequest = z.infer<typeof RondeVastleggenRequest>

/**
 * Een plek erbij in een doorlopende ronde (besluit 10): een bouwblok dat al in de ronde staat, met één
 * uitvraagmoment en het id van de plek. Het verandert niets aan wat er staat.
 */
export const PlekToevoegenRequest = z.strictObject({
  blok: Block,
  plekId: ProcessysteemId,
  aanduiding: UitvraagAanduiding,
  herhaalbaar: z.boolean().optional(),
})
export type PlekToevoegenRequest = z.infer<typeof PlekToevoegenRequest>

/**
 * Een plek zoals de opslag haar geeft: een combinatie van bouwblok en uitvraagmoment in een ronde, met het
 * id van het processysteem (besluit 1, 2). De publicatie op de plek staat niet op de plek zelf maar volgt uit
 * ronde, blok en uitvraagmoment (D-67: hoogstens één); `publicatieId` is hier alleen gevuld als de opslag haar
 * erbij zoekt. Een plek wordt nooit gewijzigd.
 */
export const Plek = z.object({
  plekId: ProcessysteemId,
  rondeId: ProcessysteemId,
  ronde: RondeSleutel,
  blok: Block,
  uitvraagRef: UitvraagAanduiding,
  herhaalbaar: z.boolean(),
  toegevoegdOp: Moment,
  publicatieId: PublicatieId.optional(),
})
export type Plek = z.infer<typeof Plek>

/** Het antwoord op het vastleggen van een ronde: de ronde met haar plekken (besluit 8: een gelijke herhaling geeft hetzelfde). */
export const RondeVastgelegd = z.object({
  rondeId: ProcessysteemId,
  sleutel: RondeSleutel,
  doorlopend: z.boolean(),
  plekken: z.array(Plek),
})
export type RondeVastgelegd = z.infer<typeof RondeVastgelegd>

/**
 * De plekken van een ronde, voor de configurator (besluit 16, 19). `rondeId` ontbreekt bij een ronde met de
 * hand: die heeft geen plekken, en de lijst is dan leeg.
 */
export const RondePlekken = z.object({
  ronde: RondeSleutel,
  rondeId: ProcessysteemId.optional(),
  doorlopend: z.boolean(),
  plekken: z.array(Plek),
})
export type RondePlekken = z.infer<typeof RondePlekken>
